import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { DealerProvider, useDealer } from '../../context/DealerContext';
import RequireDealer from '../../components/dealer/RequireDealer';
import DealerBlockedPage from './DealerBlockedPage';
import DealerDashboardPage from './DealerDashboardPage';
import DealerCreditPage from './DealerCreditPage';
import RequestCreditPage from './RequestCreditPage';
import BuyPackPage from './BuyPackPage';
import AssignCreditPage from './AssignCreditPage';
import DealerBookingsPage from './DealerBookingsPage';
import NewDealerBookingPage from './NewDealerBookingPage';
import DealerBookingDetailPage from './DealerBookingDetailPage';
import DealerDocumentsPage from './DealerDocumentsPage';
import DealerSettingsPage from './DealerSettingsPage';
import type { DealerAuth } from './types';

/**
 * Spec 085 (§1, §4) — every `/dealer/*` screen except the login. Signed-out → `/dealer/login`; a garage
 * (non-dealer) account → `/dashboard`; dealers go through `RequireDealer` (ACTIVE membership, switch on,
 * agreement accepted). Buying packs and assigning from the balance also need the wallet switch (`WalletOnly`).
 */
export default function DealerRoutes({ auth }: { auth: DealerAuth }) {
  if (!auth.isAuthenticated) return <Navigate to="/dealer/login" replace />;
  if (!auth.isDealer) return <Navigate to="/dashboard" replace />;

  return (
    <DealerProvider token={auth.token}>
      <Routes>
        <Route path="blocked" element={<DealerBlockedPage auth={auth} />} />
        <Route element={<RequireDealer auth={auth} />}>
          <Route index element={<Navigate to="/dealer/dashboard" replace />} />
          <Route path="dashboard" element={<DealerDashboardPage />} />
          <Route path="credit" element={<DealerCreditPage />} />
          <Route path="credit/request" element={<RequestCreditPage />} />
          <Route element={<WalletOnly />}>
            <Route path="credit/packs" element={<BuyPackPage />} />
            <Route path="credit/assign" element={<AssignCreditPage />} />
          </Route>
          <Route path="bookings" element={<DealerBookingsPage />} />
          <Route path="bookings/new" element={<NewDealerBookingPage />} />
          <Route path="bookings/:id" element={<DealerBookingDetailPage />} />
          <Route path="documents" element={<DealerDocumentsPage />} />
          <Route path="settings" element={<DealerSettingsPage />} />
          <Route path="*" element={<Navigate to="/dealer/dashboard" replace />} />
        </Route>
      </Routes>
    </DealerProvider>
  );
}

/**
 * The wallet surfaces (packs, assign from balance) exist only while `garagat.dealer-credit.wallet.enabled` is on;
 * otherwise a direct link lands on the Credit page, which still offers per-buyer requests.
 */
function WalletOnly() {
  const { walletEnabled } = useDealer();
  return walletEnabled ? <Outlet /> : <Navigate to="/dealer/credit" replace />;
}
